import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, HeartPulse, Factory, ShoppingCart, Truck, HardHat,
  CircleDollarSign, Building2, BellRing, Sprout, Landmark, HeartHandshake,
  Utensils, Pill, Zap, PlayCircle, Car, ShieldCheck,
  Award, SlidersHorizontal, Cpu, Globe, Briefcase, Users, ThumbsUp,
  LayoutGrid, Clock, Headphones, ArrowRight, ChevronRight,
} from 'lucide-react';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

const HERO_IMAGE = {
  desktopWidth: '750px',
  desktopHeight: 'auto',
  offsetX: '0px',
  offsetY: '0px',
};

/* ============================== DATA ============================== */

const FEATURES = [
  { Icon: Award, top: 'Industry', bottom: 'Expertise' },
  { Icon: SlidersHorizontal, top: 'Tailored', bottom: 'Solutions' },
  { Icon: ShieldCheck, top: 'Scalable &', bottom: 'Secure' },
  { Icon: Cpu, top: 'Future-Ready', bottom: 'Technology' },
];

const INDUSTRIES = [
  { Icon: GraduationCap, color: '#3284c7', title: 'Education', desc: 'School, College, University ERP, LMS, Examinations, Hostel & more.', link: '/Education' },
  { Icon: HeartPulse, color: '#16a34a', title: 'Healthcare', desc: 'Hospital, Clinic, Pharmacy, Lab, Blood Bank & Telemedicine.', link: '/Healthcare' },
  { Icon: Factory, color: '#9333ea', title: 'Manufacturing', desc: 'Production, Quality, Inventory, Supply Chain, Maintenance & more.', link: '/Manufacturing' },
  { Icon: ShoppingCart, color: '#f29132', title: 'Retail & POS', desc: 'POS Billing, Inventory, CRM, Loyalty Programs & Analytics.', link: '/Retail' },
  { Icon: Truck, color: '#2563eb', title: 'Logistics & Transport', desc: 'Fleet Management, GPS Tracking, Route Planning, Transport ERP.', link: '/Logistics' },
  { Icon: HardHat, color: '#f59e0b', title: 'Construction', desc: 'Project Management, Material Tracking, Contractor Management.', link: '/Construction' },
  { Icon: CircleDollarSign, color: '#16a34a', title: 'Finance', desc: 'Accounting, GST, Billing, Payroll, Expense & Financial Reports.', link: '/Finance' },
  { Icon: Building2, color: '#9333ea', title: 'Real Estate', desc: 'Property Management, CRM, Sales, Leasing & Rent Management.', link: '/RealEstate' },
  { Icon: BellRing, color: '#ec4899', title: 'Hospitality', desc: 'Hotel, Resort, Restaurant Management, POS, Housekeeping.', link: '/Hospitality' },
  { Icon: Sprout, color: '#16a34a', title: 'Agriculture', desc: 'Farm Management, Crop Tracking, Inventory, Supply Chain.', link: '/Agriculture' },
  { Icon: Landmark, color: '#2563eb', title: 'Government', desc: 'e-Governance, Citizen Services, Digital Offices, Smart Administration.', link: '/Government' },
  { Icon: HeartHandshake, color: '#e11d48', title: 'NGO / Non-Profit', desc: 'Donation Management, Volunteer Management, Project Tracking.', link: '/NGO' },
  { Icon: Utensils, color: '#f59e0b', title: 'Food Processing', desc: 'Procurement, Production, Batch Tracking, Warehouse & Distribution.', link: '/FoodProcessing' },
  { Icon: Pill, color: '#16a34a', title: 'Pharmaceutical', desc: 'Production, Inventory, Quality Control, Distribution, Regulatory Compliance.', link: '/Pharmaceutical' },
  { Icon: Zap, color: '#2563eb', title: 'Energy & Utilities', desc: 'Asset Management, Meter Reading, Billing, Maintenance & Reporting.', link: '/EnergyUtilities' },
  { Icon: PlayCircle, color: '#ec4899', title: 'Media & Entertainment', desc: 'Content Management, Subscription, CRM, Analytics.', link: '/MediaEntertainment' },
  { Icon: Car, color: '#9333ea', title: 'Automotive', desc: 'Workshop Management, Inventory, Service, CRM & Spare Parts.', link: '/Automotive' },
  { Icon: ShieldCheck, color: '#16a34a', title: 'Insurance', desc: 'Policy Management, Claims, Customer Management, Reports & Analytics.', link: '/Insurance' },
];

const STATS = [
  { Icon: LayoutGrid, value: '5+', label: 'Industries Served' },
  { Icon: Briefcase, value: '5+', label: 'Projects Delivered' },
  { Icon: Users, value: '10+', label: 'Expert Professionals' },
  { Icon: Globe, value: '1+', label: 'Countries Worldwide' },
  { Icon: ThumbsUp, value: '99%', label: 'Client Satisfaction' },
];

const CUSTOM_POINTS = [
  { Icon: SlidersHorizontal, label: 'Tailored Solutions' },
  { Icon: Clock, label: 'On-Time Delivery' },
  { Icon: ShieldCheck, label: 'Scalable & Secure' },
  { Icon: Headphones, label: 'Dedicated Support' },
];

/* ============================== HELPERS ============================== */

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ============================== PAGE ============================== */

const Industries = () => {
  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 600px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-8 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="se-fade-up">
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-5">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Industries</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.08] tracking-tight" style={{ color: '#13294b' }}>
                Software Solutions for{' '}
                <span className="bg-gradient-to-r from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Every Industry</span>
              </h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                SmartEdz delivers intelligent, scalable and industry-specific software solutions to help businesses
                streamline operations, improve productivity and drive growth.
              </p>

              <div className="grid grid-cols-4 gap-3 mt-8 max-w-lg">
                {FEATURES.map((f) => (
                  <div key={f.bottom} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-2">
                      <f.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-xs leading-tight">{f.top}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{f.bottom}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden sm:block se-fade-up relative lg:min-h-[520px]" style={{ animationDelay: '.15s' }}>
              <img
                src="/Industries_Img.png"
                alt="SmartEdz industries preview"
                className="se-hero-img w-full h-auto max-w-[860px] mx-auto block select-none drop-shadow-2xl"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY SOLUTIONS ================= */}
      <section className={`${CONTAINER} py-14`}>
        <Reveal>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Our Industry Solutions</h2>
            <p className="mt-3 text-slate-500">Powerful software built to meet the unique challenges of every industry</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {INDUSTRIES.map((it, i) => (
            <Reveal key={it.title} delay={(i % 6) * 50}>
              <div className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${it.color}1A` }}>
                  <it.Icon className="w-6 h-6" style={{ color: it.color }} />
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{it.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{it.desc}</p>
                <Link to={it.link} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                  Explore Solutions <ArrowRight className="w-4 h-4" />
                </Link>
                
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= STATS BANNER ================= */}
      <section className={`${CONTAINER} pb-14`}>
        <Reveal className="rounded-2xl bg-gradient-to-r from-[#2b6fb0] to-[#3284c7] shadow-lg px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-8 divide-y md:divide-y-0 lg:divide-x divide-white/15">
            {STATS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 justify-center px-2">
                <div className="w-11 h-11 rounded-xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center shrink-0">
                  <s.Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-white leading-none">{s.value}</div>
                  <div className="text-xs text-white/80 mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= CUSTOM SOLUTION BANNER ================= */}
      <section className={`${CONTAINER} pb-16`}>
        <div className="rounded-2xl bg-gradient-to-r from-[#eef4fb] to-[#e8f0fb] ring-1 ring-primary/10 px-6 sm:px-10 py-8 grid lg:grid-cols-2 gap-8 items-center">
          <div className="flex items-center gap-5 text-center sm:text-left">
            <div className="w-16 h-16 rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 flex items-center justify-center shrink-0">
              <LayoutGrid className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-extrabold" style={{ color: '#13294b' }}>Don't see your industry?</h3>
              <p className="text-slate-500 text-sm mt-1 mb-4">We build custom software for every business need.</p>
              <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white bg-primary shadow-lg shadow-primary/30 hover:brightness-110 transition">
                Request Custom Solution <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {CUSTOM_POINTS.map((p) => (
              <div key={p.label} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                <span className="w-9 h-9 rounded-lg bg-white ring-1 ring-slate-100 flex items-center justify-center shrink-0">
                  <p.Icon className="w-4 h-4 text-primary" />
                </span>
                {p.label}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;